import type { Attribution } from '@tiptap-pro/extension-snapshot-compare';
import { ChangeTrackingTransform } from '@tiptap-pro/extension-snapshot-compare';
import { Fragment, Mark, type Node, type Schema } from '@tiptap/pm/model';
import type { JSONContent } from '@tiptap/react';
import * as Y from 'yjs';

/**
 * Document compare: turn (doc A, doc B) into a ChangeTrackingTransform whose
 * `doc` equals doc B and whose attributed steps `toDiff()` can render.
 *
 * ---------------------------------------------------------------------------
 * POSITION MODEL — the whole file depends on exactly one rule
 * ---------------------------------------------------------------------------
 * For every node, `pos` is the position *of the node itself* (the position
 * immediately before it), which is what `doc.descendants((node, pos) => …)`
 * and `doc.nodeAt(pos)` use. From that one convention everything else follows:
 *
 *   content start of a node at pos ....... pos + 1
 *   position of child at content offset .. pos + 1 + offset
 *   full range of a node at pos .......... [pos, pos + node.nodeSize)
 *   inline char at offset o in textblock . pos + 1 + o
 *
 * `doc` has no position of its own; its content starts at 0. That is expressed
 * by giving the doc the virtual position -1, so `pos + 1 + offset` keeps working
 * with no special case. There are no other special cases: no `pos === 1`
 * branches, no doc/textblock/container variants, no clamping.
 *
 * The previous implementation used `parentPos + 1 + offset` for doc children,
 * which made every block `pos` one too large (it pointed at the node's content
 * start rather than the node). Delete ranges then became
 * [pos + 1, pos + 1 + nodeSize), i.e. they started inside the node and ran
 * through the *opening token of the next sibling* — an unbalanced range. PM
 * repaired it by joining, and the diff decoration landed inside the following
 * sibling's paragraph. That is the mapping error this rewrite removes.
 *
 * Invariant worth keeping: every delete/replace range must lie inside a single
 * parent's content, i.e. `doc.resolve(from).sameParent(doc.resolve(to))`. The
 * test suite asserts this for every fixture.
 *
 * ---------------------------------------------------------------------------
 * MATCHING
 * ---------------------------------------------------------------------------
 * Block children are aligned with an LCS over identity keys (`attrs.id`), so
 * matches are always monotonic — no crossing pairs, therefore no overlapping
 * ops. Nodes without ids are paired positionally inside the gaps between
 * anchors, but only against other id-less nodes of the same type: an id is an
 * identity, so a changed id is a different node (delete + insert), not an edit.
 *
 * Inline content is diffed as a token stream (one token per character, one per
 * inline atom) with an LCS, which handles mark changes, edits around atoms and
 * edits split across several text nodes without any per-node-type special
 * casing.
 *
 * ---------------------------------------------------------------------------
 * APPLICATION
 * ---------------------------------------------------------------------------
 * All ops are collected in doc-A coordinates, then applied in descending
 * position order so no op invalidates another's positions. Ties are broken
 * delete → set-attr → insert (a delete must run before an insert at the same
 * position, or the delete would eat the freshly inserted content), and equal
 * inserts are applied in reverse generation order so document order survives.
 */

const DEFAULT_BASELINE_USER_ID = 'original-document';
const DEFAULT_CURRENT_USER_ID = 'modified-document';

export type DocumentCompareAttributionLabels = {
    baselineUserId?: string;
    currentUserId?: string;
};

export function formatDocumentCompareVersionLabel(version: number, name?: string): string {
    const trimmedName = name?.trim();
    if (trimmedName) {
        return `V${version} - ${trimmedName}`;
    }

    return `V${version}`;
}

/** Guard for the O(n·m) LCS tables; beyond this a whole run becomes one replace. */
const MAX_DIFF_CELLS = 250_000;

/**
 * Character LCS happily matches incidental letters shared by two unrelated
 * words: "linear system" → "simultaneous equations" comes back as a dozen
 * one-character islands, which renders as confetti. A matched run shorter than
 * this, with edits on both sides, is dropped so the rewrite reads as one edit.
 */
const MIN_INLINE_MATCH_RUN = 4;

let syntheticIdClock = 0;
let compareAttributionLabels: Required<DocumentCompareAttributionLabels> = {
    baselineUserId: DEFAULT_BASELINE_USER_ID,
    currentUserId: DEFAULT_CURRENT_USER_ID,
};

function syntheticYId(): Y.ID {
    syntheticIdClock += 1;
    return new Y.ID(9001, syntheticIdClock);
}

function addedAttribution(): Attribution {
    return {
        type: 'added',
        userId: compareAttributionLabels.currentUserId,
        userName: compareAttributionLabels.currentUserId,
        id: syntheticYId(),
    };
}

function removedAttribution(): Attribution {
    return {
        type: 'removed',
        userId: compareAttributionLabels.baselineUserId,
        userName: compareAttributionLabels.baselineUserId,
        id: syntheticYId(),
    };
}

export type CompareOp =
    | { kind: 'delete'; from: number; to: number }
    | { kind: 'insert'; pos: number; content: Fragment }
    | { kind: 'replace-node'; from: number; to: number; content: Fragment }
    /** All attribute changes for one node, coalesced — see applyCompareOps. */
    | { kind: 'set-attr'; pos: number; attrs: Record<string, unknown> };

/* -------------------------------------------------------------------------- */
/* small helpers                                                              */
/* -------------------------------------------------------------------------- */

function childArray(fragment: Fragment): Node[] {
    const nodes: Node[] = [];
    fragment.forEach(node => {
        nodes.push(node);
    });
    return nodes;
}

/** Identity of a node, or null when it has no usable UniqueID. */
function nodeId(node: Node): string | null {
    const id = node.attrs?.id;
    return typeof id === 'string' && id.length > 0 ? id : null;
}

function attrValuesEqual(a: unknown, b: unknown): boolean {
    if (a === b) {
        return true;
    }
    if ((a ?? null) === (b ?? null)) {
        return true;
    }
    if (typeof a === 'object' && a !== null && typeof b === 'object' && b !== null) {
        return JSON.stringify(a) === JSON.stringify(b);
    }
    return false;
}

/* -------------------------------------------------------------------------- */
/* generic LCS                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Longest common subsequence, returned as monotonically increasing index pairs.
 * Common prefix and suffix are trimmed first, which makes the usual case (one
 * small edit in a large sequence) linear.
 */
function lcsPairs<T>(a: readonly T[], b: readonly T[], eq: (x: T, y: T) => boolean): [number, number][] {
    const head: [number, number][] = [];
    let lo = 0;
    while (lo < a.length && lo < b.length && eq(a[lo], b[lo])) {
        head.push([lo, lo]);
        lo += 1;
    }

    let hiA = a.length;
    let hiB = b.length;
    const tail: [number, number][] = [];
    while (hiA > lo && hiB > lo && eq(a[hiA - 1], b[hiB - 1])) {
        hiA -= 1;
        hiB -= 1;
        tail.push([hiA, hiB]);
    }
    tail.reverse();

    const n = hiA - lo;
    const m = hiB - lo;
    const middle: [number, number][] = [];

    if (n > 0 && m > 0 && n * m <= MAX_DIFF_CELLS) {
        const table: Uint32Array[] = [];
        for (let i = 0; i <= n; i += 1) {
            table.push(new Uint32Array(m + 1));
        }
        for (let i = n - 1; i >= 0; i -= 1) {
            for (let j = m - 1; j >= 0; j -= 1) {
                table[i][j] = eq(a[lo + i], b[lo + j]) ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1]);
            }
        }
        let i = 0;
        let j = 0;
        while (i < n && j < m) {
            if (eq(a[lo + i], b[lo + j])) {
                middle.push([lo + i, lo + j]);
                i += 1;
                j += 1;
            } else if (table[i + 1][j] >= table[i][j + 1]) {
                i += 1;
            } else {
                j += 1;
            }
        }
    }

    return [...head, ...middle, ...tail];
}

/* -------------------------------------------------------------------------- */
/* block child matching                                                       */
/* -------------------------------------------------------------------------- */

function sameBlockIdentity(a: Node, b: Node): boolean {
    const idA = nodeId(a);
    const idB = nodeId(b);
    if (idA !== null || idB !== null) {
        return idA !== null && idA === idB;
    }
    return a.type === b.type && a.eq(b);
}

/**
 * Align two sibling lists. Pass 1 anchors on identity (UniqueID, or a fully
 * identical id-less node). Pass 2 pairs leftover id-less siblings of the same
 * type positionally inside each gap, so an edited id-less paragraph produces an
 * inline edit rather than delete + insert.
 */
function matchBlockChildren(nodesA: readonly Node[], nodesB: readonly Node[]): [number, number][] {
    const anchors = lcsPairs(nodesA, nodesB, sameBlockIdentity);
    const pairs: [number, number][] = [];

    let nextA = 0;
    let nextB = 0;

    const pairGap = (endA: number, endB: number): void => {
        const length = Math.min(endA - nextA, endB - nextB);
        for (let k = 0; k < length; k += 1) {
            const a = nodesA[nextA + k];
            const b = nodesB[nextB + k];
            if (nodeId(a) === null && nodeId(b) === null && a.type === b.type) {
                pairs.push([nextA + k, nextB + k]);
            }
        }
    };

    for (const [anchorA, anchorB] of anchors) {
        pairGap(anchorA, anchorB);
        pairs.push([anchorA, anchorB]);
        nextA = anchorA + 1;
        nextB = anchorB + 1;
    }
    pairGap(nodesA.length, nodesB.length);

    pairs.sort((x, y) => x[0] - y[0]);
    return pairs;
}

/* -------------------------------------------------------------------------- */
/* inline tokens                                                              */
/* -------------------------------------------------------------------------- */

type InlineToken =
    | { kind: 'char'; ch: string; marks: readonly Mark[]; pos: number; size: number }
    | { kind: 'node'; node: Node; marks: readonly Mark[]; pos: number; size: number };

/** One token per character and per inline atom, carrying its doc position. */
function tokenizeInline(fragment: Fragment, start: number): InlineToken[] {
    const tokens: InlineToken[] = [];
    let pos = start;
    fragment.forEach(node => {
        if (node.isText) {
            const text = node.text ?? '';
            // Iterating by code point keeps surrogate pairs together; `size`
            // stays in UTF-16 units, which is what PM positions count.
            for (const ch of text) {
                tokens.push({ kind: 'char', ch, marks: node.marks, pos, size: ch.length });
                pos += ch.length;
            }
            return;
        }
        tokens.push({ kind: 'node', node, marks: node.marks, pos, size: node.nodeSize });
        pos += node.nodeSize;
    });
    return tokens;
}

/**
 * Discard short matched runs that sit between edits on both sides. Atoms matched
 * by id are always kept — they are identity, not coincidence — and runs at the
 * very start or end are kept so shared prefixes and suffixes survive intact.
 */
function dropIncidentalMatches(
    pairs: readonly [number, number][],
    tokensA: readonly InlineToken[],
    tokensB: readonly InlineToken[],
): [number, number][] {
    type Run = { pairs: [number, number][]; startA: number; startB: number; endA: number; endB: number };
    const runs: Run[] = [];
    for (const [indexA, indexB] of pairs) {
        const current = runs[runs.length - 1];
        if (current && current.endA === indexA && current.endB === indexB) {
            current.pairs.push([indexA, indexB]);
            current.endA = indexA + 1;
            current.endB = indexB + 1;
            continue;
        }
        runs.push({ pairs: [[indexA, indexB]], startA: indexA, startB: indexB, endA: indexA + 1, endB: indexB + 1 });
    }

    const kept: [number, number][] = [];
    runs.forEach((run, index) => {
        const previous = runs[index - 1];
        const next = runs[index + 1];
        const gapBefore = run.startA - (previous ? previous.endA : 0) + (run.startB - (previous ? previous.endB : 0));
        const gapAfter = (next ? next.startA : tokensA.length) - run.endA + ((next ? next.startB : tokensB.length) - run.endB);
        const holdsAtom = run.pairs.some(([indexA]) => tokensA[indexA].kind === 'node');
        const isIncidental = !holdsAtom && gapBefore > 0 && gapAfter > 0 && run.pairs.length < MIN_INLINE_MATCH_RUN;
        if (!isIncidental) {
            kept.push(...run.pairs);
        }
    });
    return kept;
}

function inlineTokensEqual(a: InlineToken, b: InlineToken): boolean {
    if (a.kind !== b.kind) {
        return false;
    }
    if (a.kind === 'char' && b.kind === 'char') {
        return a.ch === b.ch && Mark.sameSet(a.marks, b.marks);
    }
    if (a.kind !== 'node' || b.kind !== 'node') {
        return false;
    }
    if (a.node.type !== b.node.type) {
        return false;
    }
    const idA = nodeId(a.node);
    const idB = nodeId(b.node);
    if (idA !== null && idB !== null) {
        // Same atom, possibly with edited attrs — align them, then diff attrs.
        return idA === idB;
    }
    if (idA !== null || idB !== null) {
        return false;
    }
    return a.node.eq(b.node);
}

/**
 * Split a token run into the inline children it represents: each atom is its own
 * child, and each maximal run of characters sharing a mark set is one text node.
 *
 * Ops are emitted one child at a time. `toDiff()` classifies by slice, and a
 * slice holding more than one child reads as *block* — so a single op covering
 * an atom plus its neighbouring space produced a `block-delete` widget in the
 * middle of a paragraph. One child per op keeps inline edits inline.
 */
function groupInlineTokens(tokens: readonly InlineToken[]): InlineToken[][] {
    const groups: InlineToken[][] = [];
    for (const token of tokens) {
        const current = groups[groups.length - 1];
        const canExtend =
            current !== undefined && token.kind === 'char' && current[0].kind === 'char' && Mark.sameSet(current[0].marks, token.marks);
        if (canExtend) {
            current.push(token);
        } else {
            groups.push([token]);
        }
    }
    return groups;
}

/** The single inline node a token group represents. */
function nodeFromInlineGroup(schema: Schema, group: readonly InlineToken[]): Node {
    const first = group[0];
    if (first.kind === 'node') {
        return first.node;
    }
    let text = '';
    for (const token of group) {
        if (token.kind === 'char') {
            text += token.ch;
        }
    }
    // Never `schema.text('')` — a group always holds at least one character.
    return schema.text(text, first.marks);
}

/* -------------------------------------------------------------------------- */
/* op collection                                                              */
/* -------------------------------------------------------------------------- */

function collectAttrOps(nodeA: Node, nodeB: Node, pos: number, ops: CompareOp[]): void {
    const keys = new Set([...Object.keys(nodeA.attrs ?? {}), ...Object.keys(nodeB.attrs ?? {})]);
    const changed: Record<string, unknown> = {};
    let hasChange = false;
    for (const key of keys) {
        if (key === 'id') {
            continue;
        }
        if (attrValuesEqual(nodeA.attrs[key], nodeB.attrs[key])) {
            continue;
        }
        changed[key] = nodeB.attrs[key];
        hasChange = true;
    }
    // One op per node, not per attribute: each setNodeAttribute call rewrites the
    // node, so a second op for the same node would map past it and update the
    // wrong one.
    if (hasChange) {
        ops.push({ kind: 'set-attr', pos, attrs: changed });
    }
}

function collectInlineOps(parentA: Node, parentPos: number, fragB: Fragment, ops: CompareOp[]): void {
    const schema = parentA.type.schema;
    const contentStart = parentPos + 1;
    const tokensA = tokenizeInline(parentA.content, contentStart);
    const tokensB = tokenizeInline(fragB, 0);

    const pairs = dropIncidentalMatches(lcsPairs(tokensA, tokensB, inlineTokensEqual), tokensA, tokensB);
    const matchedA = new Map<number, number>(pairs);
    const matchedB = new Set<number>(pairs.map(([, j]) => j));

    let i = 0;
    let j = 0;
    /** Position in doc A of the next unconsumed A token. */
    let aPos = contentStart;

    while (i < tokensA.length || j < tokensB.length) {
        if (matchedA.get(i) === j) {
            const tokenA = tokensA[i];
            const tokenB = tokensB[j];
            if (tokenA.kind === 'node' && tokenB.kind === 'node' && !tokenA.node.eq(tokenB.node)) {
                collectNodeOps(tokenA.node, tokenB.node, aPos, ops);
            }
            aPos += tokenA.size;
            i += 1;
            j += 1;
            continue;
        }

        const deleted: InlineToken[] = [];
        while (i < tokensA.length && !matchedA.has(i)) {
            deleted.push(tokensA[i]);
            i += 1;
        }
        if (deleted.length > 0) {
            for (const group of groupInlineTokens(deleted)) {
                const last = group[group.length - 1];
                ops.push({ kind: 'delete', from: group[0].pos, to: last.pos + last.size });
            }
            const last = deleted[deleted.length - 1];
            aPos = last.pos + last.size;
        }

        const inserted: InlineToken[] = [];
        while (j < tokensB.length && !matchedB.has(j)) {
            inserted.push(tokensB[j]);
            j += 1;
        }
        for (const group of groupInlineTokens(inserted)) {
            ops.push({ kind: 'insert', pos: aPos, content: Fragment.from(nodeFromInlineGroup(schema, group)) });
        }

        if (deleted.length === 0 && inserted.length === 0) {
            // Unreachable while LCS pairs are monotonic; guards against a stuck loop.
            if (i < tokensA.length) {
                aPos += tokensA[i].size;
                i += 1;
            } else {
                j += 1;
            }
        }
    }
}

function collectBlockChildrenOps(parentA: Node, parentPos: number, fragB: Fragment, ops: CompareOp[]): void {
    const contentStart = parentPos + 1;
    const entriesA: { node: Node; pos: number; index: number }[] = [];
    parentA.content.forEach((node, offset, index) => {
        entriesA.push({ node, pos: contentStart + offset, index });
    });
    const nodesA = entriesA.map(entry => entry.node);
    const nodesB = childArray(fragB);

    const pairs = matchBlockChildren(nodesA, nodesB);
    const aToB = new Map<number, number>(pairs);
    const bToA = new Map<number, number>(pairs.map(([a, b]) => [b, a]));

    // 1. delete unmatched A children over their own full node range
    for (const entry of entriesA) {
        if (!aToB.has(entry.index)) {
            ops.push({ kind: 'delete', from: entry.pos, to: entry.pos + entry.node.nodeSize });
        }
    }

    // 2. recurse into matched pairs
    for (const entry of entriesA) {
        const indexB = aToB.get(entry.index);
        if (indexB !== undefined) {
            collectNodeOps(entry.node, nodesB[indexB], entry.pos, ops);
        }
    }

    // 3. insert runs of unmatched B children just after the previous match
    let cursor = contentStart;
    let run: Node[] = [];
    const flushRun = (): void => {
        // One op per sibling: a slice with several children is classified as a
        // single block diff, which hides which node was actually added.
        for (const node of run) {
            ops.push({ kind: 'insert', pos: cursor, content: Fragment.from(node) });
        }
        run = [];
    };
    nodesB.forEach((nodeB, indexB) => {
        const indexA = bToA.get(indexB);
        if (indexA !== undefined) {
            flushRun();
            const entry = entriesA[indexA];
            cursor = entry.pos + entry.node.nodeSize;
            return;
        }
        run.push(nodeB);
    });
    flushRun();
}

function collectNodeOps(nodeA: Node, nodeB: Node, pos: number, ops: CompareOp[]): void {
    if (nodeA.eq(nodeB)) {
        return;
    }

    const replaceWholeNode = (): void => {
        ops.push({ kind: 'replace-node', from: pos, to: pos + nodeA.nodeSize, content: Fragment.from(nodeB) });
    };

    // Different type, different marks, or a bare text child that the parent
    // walker did not handle: swap the node. `toDiff()` then classifies from the
    // slice, so a block node yields block-delete/insert and an inline node
    // yields inline-delete/insert. Types are never forced.
    if (nodeA.type !== nodeB.type || !Mark.sameSet(nodeA.marks, nodeB.marks) || nodeA.isText) {
        replaceWholeNode();
        return;
    }

    const attrOps: CompareOp[] = [];
    collectAttrOps(nodeA, nodeB, pos, attrOps);

    // Atoms (image, hardBreak, inline math, horizontalRule…): an attribute
    // update if that is the only difference, otherwise swap the node.
    if (nodeA.isLeaf) {
        if (attrOps.length > 0) {
            ops.push(...attrOps);
        } else {
            replaceWholeNode();
        }
        return;
    }

    ops.push(...attrOps);

    // Recursion is chosen by node *kind*, never by "does a descendant have an
    // id" — that question has nothing to do with how the node must be walked.
    if (nodeA.isTextblock || nodeA.isInline) {
        collectInlineOps(nodeA, pos, nodeB.content, ops);
        return;
    }
    collectBlockChildrenOps(nodeA, pos, nodeB.content, ops);
}

/* -------------------------------------------------------------------------- */
/* ordering + application                                                     */
/* -------------------------------------------------------------------------- */

function opPos(op: CompareOp): number {
    return op.kind === 'insert' || op.kind === 'set-attr' ? op.pos : op.from;
}

/** Content ops run first (pass 0), attribute updates last (pass 1). */
function opPass(op: CompareOp): number {
    return op.kind === 'set-attr' ? 1 : 0;
}

/** Within the content pass, insert before delete/replace at the same position. */
function opPriority(op: CompareOp): number {
    return op.kind === 'insert' ? 1 : 0;
}

function sortCompareOps(ops: readonly CompareOp[]): CompareOp[] {
    return ops
        .map((op, seq) => ({ op, seq }))
        .sort((x, y) => {
            const passDelta = opPass(x.op) - opPass(y.op);
            if (passDelta !== 0) {
                return passDelta;
            }
            if (opPass(x.op) === 1) {
                // Attribute pass: innermost (highest position) first, so an outer
                // node is rebuilt around already-updated children.
                return opPos(y.op) - opPos(x.op);
            }
            const posDelta = opPos(x.op) - opPos(y.op);
            if (posDelta !== 0) {
                return posDelta;
            }
            const priorityDelta = opPriority(y.op) - opPriority(x.op);
            if (priorityDelta !== 0) {
                return priorityDelta;
            }
            // Equal inserts stay in document order.
            return x.seq - y.seq;
        })
        .map(entry => entry.op);
}

/**
 * Collect every op needed to turn doc A into doc B, already in application order
 * (content ops ascending, then attribute updates descending).
 * Exported for tests: the suite asserts range invariants on these ops.
 */
export function collectDocumentCompareOps(docA: Node, docB: Node): CompareOp[] {
    const ops: CompareOp[] = [];
    // The doc has no position of its own; -1 makes `pos + 1 + offset` yield 0
    // for its first child.
    collectBlockChildrenOps(docA, -1, docB.content, ops);
    return sortCompareOps(ops);
}

function applyCompareOps(tr: ChangeTrackingTransform, ops: readonly CompareOp[]): void {
    // Positions are collected against doc A; mapping carries them into the
    // current doc as earlier (lower-position) steps shift things right or left.
    const start = (pos: number): number => tr.mapping.map(pos, 1);
    const end = (pos: number): number => tr.mapping.map(pos, -1);

    for (const op of ops) {
        switch (op.kind) {
            case 'delete': {
                const from = start(op.from);
                const to = end(op.to);
                if (from < to) {
                    tr.deleteWithAttribution(from, to, removedAttribution());
                }
                break;
            }
            case 'insert':
                tr.insertWithAttribution(start(op.pos), op.content, addedAttribution());
                break;
            case 'replace-node': {
                // Insert first, then drop the old node (now shifted right by the
                // inserted size, which `start`/`end` recompute). If your version
                // of @tiptap-pro/extension-snapshot-compare exposes
                // replaceWithAttribution, this is the one place to use it.
                tr.insertWithAttribution(start(op.from), op.content, addedAttribution());
                const from = start(op.from);
                const to = end(op.to);
                if (from < to) {
                    tr.deleteWithAttribution(from, to, removedAttribution());
                }
                break;
            }
            case 'set-attr': {
                // Resolve the position once. Each call rewrites the node at `at`
                // without changing its size, so `at` stays valid for the rest of
                // them, while mapping again would walk past the node.
                const at = start(op.pos);
                for (const [key, value] of Object.entries(op.attrs)) {
                    tr.setNodeAttributeWithAttribution(at, key, value, addedAttribution());
                }
                break;
            }
        }
    }
}

export function buildDocumentCompareTransform(
    schema: Schema,
    jsonA: JSONContent,
    jsonB: JSONContent,
    attributionLabels?: DocumentCompareAttributionLabels,
): ChangeTrackingTransform {
    syntheticIdClock = 0;
    compareAttributionLabels = {
        baselineUserId: attributionLabels?.baselineUserId ?? DEFAULT_BASELINE_USER_ID,
        currentUserId: attributionLabels?.currentUserId ?? DEFAULT_CURRENT_USER_ID,
    };
    const docA = schema.nodeFromJSON(jsonA);
    const docB = schema.nodeFromJSON(jsonB);
    const tr = new ChangeTrackingTransform(docA);
    applyCompareOps(tr, collectDocumentCompareOps(docA, docB));
    return tr;
}

export function parseCompareJsonInput(text: string): JSONContent {
    const parsed = JSON.parse(text) as unknown;
    if (!parsed || typeof parsed !== 'object' || (parsed as JSONContent).type !== 'doc') {
        throw new Error('JSON must be a ProseMirror document with type "doc".');
    }
    return parsed as JSONContent;
}
